import { useState, useRef, useMemo } from 'react';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { FilePreviewModal } from '@/components/ui/FilePreviewModal';
import { downloadPrivateFile } from '@/lib/download';
import { ApiRequestError } from '@/lib/apiClient';
import type { StudentHomework, StudentHomeworkList } from '@/types/student';
import { studentApi } from '../api/student.api';
import { previousDay, todayString, weekOf } from '../utils';
import {
  HomeworkHeader,
  HomeworkStatCards,
  HomeworkFilterBar,
  HomeworkRosterSection,
  TaskCalendarWidget,
  DueTodayWidget,
  StudyTipWidget,
  HomeworkRepoModal,
  HomeworkDetailsModal,
  HomeworkSubmitModal,
  ActivitySubmitModal,
  HAND_IN_ACCEPT,
  workFromActivity,
  workFromHomework,
  workKey,
  HOMEWORK_CATEGORIES,
  HOMEWORK_SORT_OPTIONS,
  QUICK_DATE_FILTERS,
  type HomeworkCategory,
  type HomeworkSort,
  type QuickDateFilter,
  type StudentWorkItem,
} from './homework';

/**
 * The list's filters, as they sit in the address bar, so a refresh finds the
 * same tab, subject, day and order. `showCompleted` is `'true'` or blank.
 */
type Filters = {
  category: HomeworkCategory;
  subject: string;
  sort: HomeworkSort;
  day: QuickDateFilter;
  date: string;
  showCompleted: string;
};

const DEFAULT_FILTERS: Filters = {
  category: 'All',
  subject: 'ALL',
  sort: 'DUE_EARLIEST',
  day: 'ALL',
  date: '',
  showCompleted: '',
};

/** Subjects are the student's own, so any name is let through — one that matches nothing shows the empty list. */
const FILTER_OPTIONS = {
  allowed: {
    category: HOMEWORK_CATEGORIES,
    sort: HOMEWORK_SORT_OPTIONS.map((option) => option.value),
    day: QUICK_DATE_FILTERS,
    showCompleted: ['true'],
  },
};

interface HomeworkTasksProps {
  list: StudentHomeworkList | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
}

/**
 * The student's work page: homework and class activities in one list.
 *
 * Two tables, two reads — the homework arrives from the page, the activities
 * are fetched here, only when the school has activities switched on. The tabs
 * split the list by which table an item came from; there is no other "kind" of
 * work to filter on, which is why the old Assignments / Projects / Tasks tabs
 * (matched on words in the title) are gone.
 */
export function HomeworkTasks({ list, isLoading, error, onRetry }: HomeworkTasksProps) {
  const features = useAuthStore((state) => state.user?.features);
  const hasActivities = Boolean(features?.includes('ACTIVITY'));
  const activitiesQuery = useApiQuery(
    () => (hasActivities ? studentApi.activities() : Promise.resolve(null)),
    [hasActivities],
  );

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  // The Activities tab is only offered when the school has activities on, so a
  // restored one falls back to All rather than showing a tab that is not there.
  const selectedCategory: HomeworkCategory =
    filters.category === 'Activities' && !hasActivities ? 'All' : filters.category;
  const { subject: selectedHwSubject, sort: sortBy, day: quickDateFilter, date: customDate } = filters;
  const showCompleted = filters.showCompleted === 'true';
  const setSelectedCategory = (category: HomeworkCategory) => setFilters({ category });
  const setSelectedHwSubject = (subject: string) => setFilters({ subject });
  const setSortBy = (sort: HomeworkSort) => setFilters({ sort });
  const setQuickDateFilter = (day: QuickDateFilter) => setFilters({ day });
  const setCustomDate = (date: string) => setFilters({ date });
  const setShowCompleted = (show: boolean) => setFilters({ showCompleted: show ? 'true' : '' });

  // Calendar widget date navigation state
  const [calendarViewDate, setCalendarViewDate] = useState<Date>(() => new Date());

  // Accordion for completed tasks

  /** The piece of work whose details are open, or null. */
  const [viewing, setViewing] = useState<StudentWorkItem | null>(null);
  /** Which card is mid-upload, by `key`, so only its own button says so. */
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  /** The activity whose hand-in dialog is open. */
  const [handingInActivity, setHandingInActivity] = useState<StudentWorkItem | null>(null);
  /** The homework whose hand-in dialog is open. */
  const [handingInHomework, setHandingInHomework] = useState<StudentWorkItem | null>(null);
  /** One hidden input, driven by whichever card was pressed. */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  /** A solution picked but not sent yet — shown for a last look before it goes. */
  const [pendingHandIn, setPendingHandIn] = useState<{ homeworkUuid: string; file: File } | null>(
    null,
  );
  /** What I handed in, opened in a preview from the details dialog. */
  const [previewingSubmission, setPreviewingSubmission] = useState<{
    path: string;
    fileName: string;
  } | null>(null);

  /** Repository submission modal state */
  const [repoFor, setRepoFor] = useState<StudentHomework | null>(null);
  const [repoUrl, setRepoUrl] = useState('');

  /** Every piece of work of both kinds, in the one shape the page draws. */
  const allWork = useMemo(
    () => [
      ...(list?.homework ?? []).map(workFromHomework),
      ...(activitiesQuery.data?.activities ?? []).map(workFromActivity),
    ],
    [list, activitiesQuery.data],
  );

  /** What the open tab holds — the figures, the calendar and the list all read this. */
  const homeworkList = useMemo(() => {
    if (selectedCategory === 'Completed Homework') {
      return allWork.filter((work) => work.submission !== null);
    }
    if (selectedCategory === 'All') {
      return allWork;
    }
    return allWork.filter((work) => work.kind === (selectedCategory === 'Homework' ? 'HOMEWORK' : 'ACTIVITY'));
  }, [allWork, selectedCategory]);

  // The subjects the open tab actually has work in, so a pill is never empty.
  const subjectPills = useMemo(
    () => ['ALL', ...[...new Set(homeworkList.map((work) => work.subject.name))].sort((a, b) => a.localeCompare(b))],
    [homeworkList],
  );
  const isListLoading = isLoading || (selectedCategory !== 'Homework' && activitiesQuery.isLoading);
  const listError =
    (selectedCategory !== 'Activities' ? error : null) ?? (selectedCategory !== 'Homework' ? activitiesQuery.error : null);

  function reload() {
    onRetry();
    if (hasActivities) activitiesQuery.refetch();
  }

  const todayStr = todayString();

  // Hands in chosen file and optional note
  async function handIn(homeworkUuid: string, file: File, notes?: string) {
    setUploadingKey(workKey('HOMEWORK', homeworkUuid));
    try {
      const saved = await studentApi.submitHomework(homeworkUuid, file, notes || undefined);
      setHandingInHomework(null);
      setPendingHandIn(null);
      toast.success('Handed in', `${saved.attachmentName} was sent to your teacher.`);
      onRetry();
    } catch (caught) {
      setPendingHandIn(null);
      toast.error(
        'Could not hand that in',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setUploadingKey(null);
    }
  }

  // Hands in an activity — the file, and the note to the teacher
  async function handInActivity(work: StudentWorkItem, file: File, notes: string) {
    setUploadingKey(work.key);
    try {
      await studentApi.submitActivity(work.uuid, file, notes || undefined);
      toast.success('Handed in', `${file.name} was sent to your teacher.`);
      setHandingInActivity(null);
      activitiesQuery.refetch();
    } catch (caught) {
      toast.error(
        'Could not hand that in',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setUploadingKey(null);
    }
  }

  // Hands in repo URL
  async function handInRepo(hw: StudentHomework, url: string) {
    setUploadingKey(workKey('HOMEWORK', hw.uuid));
    try {
      await studentApi.submitHomeworkRepo(hw.uuid, url.trim());
      toast.success('Handed in', 'Your teacher can now open your repository.');
      setRepoFor(null);
      setRepoUrl('');
      onRetry();
    } catch (caught) {
      toast.error(
        'Could not hand that in',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setUploadingKey(null);
    }
  }

  // Download the sheet the teacher handed out — a worksheet, or an activity's guidelines
  async function downloadWorksheet(work: StudentWorkItem) {
    if (!work.attachment) return;
    try {
      await downloadPrivateFile(
        work.kind === 'ACTIVITY' ? studentApi.activityGuidelinePath(work.uuid) : studentApi.worksheetPath(work.uuid),
        work.attachment.fileName,
      );
    } catch {
      toast.error('Could not download that file', 'Please try again.');
    }
  }

  function submissionPath(work: StudentWorkItem): string {
    return work.kind === 'ACTIVITY'
      ? studentApi.activitySubmissionFilePath(work.uuid)
      : studentApi.submissionFilePath(work.uuid);
  }

  // Download submitted solution
  async function downloadSubmission(work: StudentWorkItem) {
    try {
      await downloadPrivateFile(submissionPath(work), work.submission?.fileName ?? 'submission');
    } catch {
      toast.error('Could not download that file', 'Please try again.');
    }
  }

  const statBaseList = useMemo(
    () => (selectedCategory === 'Completed Homework' ? allWork : homeworkList),
    [allWork, homeworkList, selectedCategory],
  );

  // Stats calculation. The first three cards say "This Week", so they count
  // only work set since Monday — counting the whole list put a term's homework
  // under a weekly label. Overdue stays all-time: it is what still needs doing,
  // whenever it was set.
  const thisWeekHomework = useMemo(() => {
    const week = weekOf(todayStr);
    return statBaseList.filter((hw) => hw.assignedOn >= week.from && hw.assignedOn <= week.to);
  }, [statBaseList, todayStr]);
  const totalAssigned = thisWeekHomework.length;
  const completedCount = thisWeekHomework.filter((hw) => hw.submission !== null).length;
  // `null` rather than 0 in a week with nothing set: there is no share to
  // speak of, and "0%" read as all of it left undone.
  const completedPercent = totalAssigned > 0 ? Math.round((completedCount / totalAssigned) * 100) : null;

  const pendingCount = totalAssigned - completedCount;
  const pendingPercent = totalAssigned > 0 ? Math.round((pendingCount / totalAssigned) * 100) : null;

  const overdueHomeworkAll = useMemo(
    () => statBaseList.filter((hw) => hw.submission === null && hw.dueDate < todayStr),
    [statBaseList, todayStr],
  );
  const overdueCount = overdueHomeworkAll.length;

  const todayHomeworkCount = useMemo(
    () => statBaseList.filter((hw) => hw.assignedOn === todayStr || hw.dueDate === todayStr).length,
    [statBaseList, todayStr],
  );

  // Items due today for the right widget
  const dueTodayTasks = useMemo(
    () => statBaseList.filter((hw) => hw.dueDate === todayStr),
    [statBaseList, todayStr],
  );

  // Filtering list based on Category, Subject, Date, and Sort
  const filteredHomework = useMemo(() => {
    return homeworkList
      .filter((hw) => {
        // Subject filter
        const matchesSubject = selectedHwSubject === 'ALL' || hw.subject.name === selectedHwSubject;
        if (!matchesSubject) return false;

        // Date filter
        let matchesDate = true;
        if (customDate) {
          matchesDate = hw.assignedOn === customDate || hw.dueDate === customDate;
        } else if (quickDateFilter === 'TODAY') {
          matchesDate = hw.assignedOn === todayStr || hw.dueDate === todayStr;
        } else if (quickDateFilter === 'YESTERDAY') {
          const yesterdayStr = previousDay(todayStr);
          matchesDate = hw.assignedOn === yesterdayStr || hw.dueDate === yesterdayStr;
        }

        return matchesDate;
      })
      .sort((a, b) => {
        if (sortBy === 'DUE_EARLIEST') return a.dueDate.localeCompare(b.dueDate);
        if (sortBy === 'DUE_LATEST') return b.dueDate.localeCompare(a.dueDate);
        if (sortBy === 'ASSIGNED_NEWEST') return b.assignedOn.localeCompare(a.assignedOn);
        if (sortBy === 'SUBJECT_AZ') return a.subject.name.localeCompare(b.subject.name);
        return 0;
      });
  }, [homeworkList, selectedHwSubject, customDate, quickDateFilter, todayStr, sortBy]);

  const isPastDate =
    quickDateFilter === 'YESTERDAY' ||
    Boolean(customDate && customDate < todayStr);

  // Split into Upcoming (not submitted & not overdue), Overdue (not submitted & overdue), and Completed (submitted)
  const upcomingList = useMemo(
    () => (selectedCategory === 'Completed Homework' ? [] : filteredHomework.filter((hw) => hw.submission === null && hw.dueDate >= todayStr)),
    [filteredHomework, todayStr, selectedCategory],
  );

  const overdueList = useMemo(
    () => (selectedCategory === 'Completed Homework' ? [] : filteredHomework.filter((hw) => hw.submission === null && hw.dueDate < todayStr)),
    [filteredHomework, todayStr, selectedCategory],
  );

  const completedFilteredList = useMemo(
    () => filteredHomework.filter((hw) => hw.submission !== null),
    [filteredHomework],
  );

  // Map dates with homework for the calendar widget
  const daysWithTasks = useMemo(() => {
    const map = new Set<string>();
    statBaseList.forEach((hw) => {
      map.add(hw.assignedOn);
      map.add(hw.dueDate);
    });
    return map;
  }, [statBaseList]);

  // Calendar calculations
  const calendarYear = calendarViewDate.getFullYear();
  const calendarMonth = calendarViewDate.getMonth();
  const calendarMonthName = calendarViewDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const calendarDays = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const totalDays = lastDay.getDate();

    // Monday as index 0 (Sun=0 -> 6, Mon=1 -> 0, etc.)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek === -1) startDayOfWeek = 6;

    const days: { dayNumber: number | null; dateStr: string | null; isToday: boolean; hasTasks: boolean }[] = [];

    // Empty offset days
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ dayNumber: null, dateStr: null, isToday: false, hasTasks: false });
    }

    // Actual month days
    for (let d = 1; d <= totalDays; d++) {
      const monthStr = String(calendarMonth + 1).padStart(2, '0');
      const dayStr = String(d).padStart(2, '0');
      const dateStr = `${calendarYear}-${monthStr}-${dayStr}`;
      days.push({
        dayNumber: d,
        dateStr,
        isToday: dateStr === todayStr,
        hasTasks: daysWithTasks.has(dateStr),
      });
    }

    return days;
  }, [calendarYear, calendarMonth, todayStr, daysWithTasks]);

  const handleCalendarDayClick = (dateStr: string | null) => {
    if (!dateStr) return;
    if (customDate === dateStr) {
      setCustomDate('');
      setQuickDateFilter('ALL');
    } else {
      setCustomDate(dateStr);
      setQuickDateFilter('CUSTOM');
    }
  };

  return (
    <div className="flex flex-col gap-5  animate-fade-in w-full">
      {/* 1. Top Header */}
      <HomeworkHeader
        todayHomeworkCount={todayHomeworkCount}
        onToggleToday={() => {
          setQuickDateFilter(quickDateFilter === 'TODAY' ? 'ALL' : 'TODAY');
          setCustomDate('');
        }}
      />

      {/* 2. Four KPI Metric Cards */}
      <HomeworkStatCards
        totalAssigned={totalAssigned}
        completedCount={completedCount}
        completedPercent={completedPercent}
        pendingCount={pendingCount}
        pendingPercent={pendingPercent}
        overdueCount={overdueCount}
      />

      {/* 3. Category Tabs & Date Filtration Bar */}
      <HomeworkFilterBar
        selectedCategory={selectedCategory}
        onSelectCategory={(category) => {
          setSelectedCategory(category);
          // A subject from one tab may have no work in the next.
          setSelectedHwSubject('ALL');
        }}
        showActivities={hasActivities}
        subjectPills={subjectPills}
        selectedSubject={selectedHwSubject}
        onSubjectChange={setSelectedHwSubject}
        quickDateFilter={quickDateFilter}
        onSelectQuickDate={(dOpt) => {
          setQuickDateFilter(dOpt);
          setCustomDate('');
        }}
        customDate={customDate}
        onCustomDateChange={(val) => {
          setCustomDate(val);
          setQuickDateFilter(val ? 'CUSTOM' : 'ALL');
        }}
      />

      {/* 4. Main Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Left Column: Task Roster (8 Cols) */}
        <HomeworkRosterSection
          upcomingList={upcomingList}
          overdueList={overdueList}
          completedList={completedFilteredList}
          isCompletedCategory={selectedCategory === 'Completed Homework'}
          isPastDate={isPastDate}
          showKindLabel={hasActivities && (selectedCategory === 'All' || selectedCategory === 'Completed Homework')}
          // The whole tab, not this week: it decides between "nothing set yet"
          // and "nothing matches your filters", and older work is still work.
          totalHomeworkCount={homeworkList.length}
          emptyNoun={selectedCategory === 'Completed Homework' ? 'completed tasks' : selectedCategory === 'Activities' ? 'activities' : selectedCategory === 'Homework' ? 'homework' : 'work'}
          sortBy={sortBy}
          onSortChange={setSortBy}
          isLoading={isListLoading}
          error={listError}
          onRetry={reload}
          showCompleted={showCompleted}
          onToggleCompleted={() => setShowCompleted(!showCompleted)}
          uploadingKey={uploadingKey}
          onDownloadWorksheet={(work) => void downloadWorksheet(work)}
          onViewDetails={setViewing}
          onOpenRepoModal={(work) => {
            if (work.source.kind !== 'HOMEWORK') return;
            setRepoFor(work.source.homework);
            setRepoUrl(work.submission?.repoUrl ?? '');
          }}
          onHandIn={(work) => {
            if (work.kind === 'ACTIVITY') {
              setHandingInActivity(work);
              return;
            }
            setHandingInHomework(work);
          }}
        />

        {/* Right Column: Sidebar Widgets (4 Cols) */}
        <div className="lg:col-span-4 flex flex-col gap-5">
          <TaskCalendarWidget
            calendarViewDate={calendarViewDate}
            calendarMonthName={calendarMonthName}
            onPrevMonth={() =>
              setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() - 1, 1))
            }
            onNextMonth={() =>
              setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() + 1, 1))
            }
            calendarDays={calendarDays}
            customDate={customDate}
            onDayClick={handleCalendarDayClick}
          />

          <DueTodayWidget
            dueTodayTasks={dueTodayTasks}
            onViewAllDueToday={() => {
              setQuickDateFilter('TODAY');
              setCustomDate('');
            }}
          />

          <StudyTipWidget />
        </div>
      </div>

      {/* Hidden File Picker For Submissions */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={HAND_IN_ACCEPT}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          // Not sent yet: the preview below asks before it goes.
          if (file && uploadFor) setPendingHandIn({ homeworkUuid: uploadFor, file });
          setUploadFor(null);
        }}
      />

      {/* Repository Submission Modal */}
      <HomeworkRepoModal
        repoFor={repoFor}
        repoUrl={repoUrl}
        onRepoUrlChange={setRepoUrl}
        onClose={() => {
          setRepoFor(null);
          setRepoUrl('');
        }}
        onSubmit={() => {
          if (repoFor) void handInRepo(repoFor, repoUrl);
        }}
        isSubmitting={repoFor !== null && uploadingKey === workKey('HOMEWORK', repoFor.uuid)}
      />

      {/* Homework Hand-in Modal */}
      <HomeworkSubmitModal
        homework={handingInHomework}
        isSubmitting={handingInHomework !== null && uploadingKey === handingInHomework.key}
        onClose={() => setHandingInHomework(null)}
        onSubmit={(file, notes) => {
          if (handingInHomework) void handIn(handingInHomework.uuid, file, notes);
        }}
      />

      {/* Activity Hand-in Modal */}
      <ActivitySubmitModal
        activity={handingInActivity}
        isSubmitting={handingInActivity !== null && uploadingKey === handingInActivity.key}
        onClose={() => setHandingInActivity(null)}
        onSubmit={(file, notes) => {
          if (handingInActivity) void handInActivity(handingInActivity, file, notes);
        }}
      />

      {/* Task Details Modal */}
      <HomeworkDetailsModal
        viewing={viewing}
        onClose={() => setViewing(null)}
        onDownloadSubmission={(work) => void downloadSubmission(work)}
        onPreviewSubmission={(work) =>
          setPreviewingSubmission({
            path: submissionPath(work),
            fileName: work.submission?.fileName ?? 'Submission',
          })
        }
      />

      <FilePreviewModal
        source={previewingSubmission ? { path: previewingSubmission.path } : null}
        fileName={previewingSubmission?.fileName ?? ''}
        onClose={() => setPreviewingSubmission(null)}
      />

      {/* The solution just picked, checked before it is sent. */}
      <FilePreviewModal
        source={pendingHandIn ? { file: pendingHandIn.file } : null}
        fileName={pendingHandIn?.file.name ?? ''}
        onClose={() => {
          if (pendingHandIn && uploadingKey === workKey('HOMEWORK', pendingHandIn.homeworkUuid)) return;
          setPendingHandIn(null);
        }}
        confirm={{
          label: 'Submit solution',
          isLoading:
            pendingHandIn !== null &&
            uploadingKey === workKey('HOMEWORK', pendingHandIn.homeworkUuid),
          onConfirm: () => {
            if (pendingHandIn) void handIn(pendingHandIn.homeworkUuid, pendingHandIn.file);
          },
        }}
      />
    </div>
  );
}
